import axiosConfig from "./axiosConfig";

export interface DbReading {
  month: string;
  generation: number;
  gridImport: number;
  gridExport: number;
  behindMeterPV: number;
  behindMeterBattery: number;
  maxPeriodGridExport?: number | null;
}

export interface FileReading {
  fileGuid: string;
  fileName: string;
  month: string;
  firstDay?: string;
  lastDay?: string;
  generation?: number;
  gridImport?: number;
  gridExport?: number;
  behindMeterPV?: number;
  behindMeterBattery?: number;
  error?: string;
}

export interface ForecastMetric {
  actual: number | null;
  p50: number | null;
  p90: number | null;
  pctVsP50: number | null;
  pctVsP90: number | null;
  ryg: "red" | "amber" | "green" | "pending";
  actualCurtailmentLoss?: number | null;
  curtailmentCoverage?: number | null;
}

export interface CustomerRevenueMetric extends ForecastMetric {
  revenueType: string;
}

export interface ForecastComparison {
  generation: ForecastMetric;
  demand: ForecastMetric;
  behindMeterPV: ForecastMetric;
  behindMeterBattery: ForecastMetric;
  export: ForecastMetric;
  customerRevenue: CustomerRevenueMetric;
}

export interface ZeroExportBreachingPeriod {
  start: string;
  end: string;
  periodCount: number;
  maxExport: number;
}

export interface ZeroExportCheck {
  isZeroExportSite: boolean;
  maxPeriodGridExport?: number;
  thresholdKwh?: number;
  hasIssue?: boolean;
  breachingPeriods?: ZeroExportBreachingPeriod[];
  totalBreachingBlocks?: number;
}

export interface PeriodIssue {
  type: "demandSpike" | "demandDropToZero" | "generationDropToZero";
  start: string;
  end: string;
  periodCount: number;
  maxValue?: number;
  severity?: "red" | "amber";
  esysLabel?: string;
}

export interface DemandSpikeSummary {
  p95Demand: number;
  thresholdRed: number;
  thresholdAmber: number;
  totalSpikePeriods: number;
  hasIssue: boolean;
}

export interface ZeroCheckSummary {
  totalPeriods?: number;
  totalExpectedGenPeriods?: number;
  skipped: boolean;
}

export interface PeriodChecks {
  periodMins: number;
  issues: PeriodIssue[];
  demandSpikeSummary: DemandSpikeSummary;
  demandDropToZeroSummary: ZeroCheckSummary;
  generationDropToZeroSummary: ZeroCheckSummary;
}

export interface MonthInfo {
  isFirstMonth: boolean;
  isPartialMonth: boolean;
  operationalDays: number | null;
}

export interface SiteReadingsValidationResponse {
  month: string;
  dbReading: DbReading;
  fileReading: FileReading | null;
  forecastComparison: ForecastComparison | null;
  zeroExportCheck: ZeroExportCheck | null;
  periodChecks: PeriodChecks | null;
  monthInfo: MonthInfo | null;
}

const getSiteReadingsValidation = async (
  authToken: string,
  siteId: string,
  fileGuid: string,
  month: string
): Promise<SiteReadingsValidationResponse> => {
  const { data } = await axiosConfig.get(
    `/site/${siteId}/readings_validation?fileGuid=${encodeURIComponent(fileGuid)}&month=${encodeURIComponent(month)}`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  );
  return data;
};

export default getSiteReadingsValidation;
